import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, Select, Flex, Input, FormControl, FormLabel, useToast, Button, Table, Thead, Tbody, Tr, Th, Td, Tag, Spinner, IconButton, Checkbox, useBreakpointValue, SimpleGrid, VStack, Stack, Icon, Menu, MenuButton, MenuList, MenuItem, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, CheckIcon, MinusIcon, AddIcon } from '@chakra-ui/icons';
import { FaEllipsisV } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import {
  ContaPagar, PagamentoConta, Ocorrencia,
  ocorrenciasDoMes, dataHojeLocal, diasAte,
} from '../utils/contas';

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function formatarMoeda(valor: number) {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatarMoedaInput(valor: string | number) {
  let v = '';
  if (typeof valor === 'number') {
    v = Math.round(valor * 100).toString();
  } else {
    v = valor.replace(/\D/g, '');
  }
  if (!v) return '0,00';
  // Garante ao menos 3 dígitos para separar centavos (0.05 → '5' → '0,05')
  while (v.length < 3) v = '0' + v;
  const centavos = v.slice(-2);
  let reais = v.slice(0, -2);
  reais = reais.replace(/^0+/, '') || '0';
  reais = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${reais},${centavos}`;
}

function moedaParaNumero(valor: string) {
  return Number(valor.replace(/\./g, '').replace(',', '.'));
}

function formatarDataBR(dataStr: string) {
  if (!dataStr) return '';
  // Aceita 'YYYY-MM-DD' e também ISO com hora, cortando o que vem depois
  const [ano, mes, dia] = dataStr.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

const ContasPagar: React.FC = () => {
  const { user } = useAuth();
  const hoje = dataHojeLocal();
  const [mes, setMes] = useState(Number(hoje.split('-')[1]) - 1);
  const [ano, setAno] = useState(Number(hoje.split('-')[0]));
  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [pagamentos, setPagamentos] = useState<PagamentoConta[]>([]);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [bancos, setBancos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dispensaOc, setDispensaOc] = useState<Ocorrencia | null>(null);
  const [payFatura, setPayFatura] = useState(false);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const formulario = useDisclosure();

  // Formulário
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [recorrente, setRecorrente] = useState(true);
  const [diaVencimento, setDiaVencimento] = useState(10);
  const [dataVencimento, setDataVencimento] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [bancoId, setBancoId] = useState('');

  // Diálogo de pagamento
  const [payOcorrencia, setPayOcorrencia] = useState<Ocorrencia | null>(null);
  const [payBancoId, setPayBancoId] = useState('');
  const [payValor, setPayValor] = useState('');
  const [payData, setPayData] = useState(dataHojeLocal());

  // Diálogo de exclusão
  const [deleteConta, setDeleteConta] = useState<ContaPagar | null>(null);
  const cancelRef = React.useRef<any>();

  const fetchContas = useCallback(async () => {
    if (!user) return;
    try {
      setContas(await api.get('/api/contas-pagar'));
    } catch (err) {
      toast({ title: 'Erro ao carregar contas', description: err instanceof Error ? err.message : undefined, status: 'error' });
      setContas([]);
    }
  }, [user, toast]);

  // Busca todos os pagamentos do usuário de uma vez: as ocorrências de qualquer
  // mês são calculadas no cliente, sem refetch (nem corrida de respostas) ao
  // trocar de mês, e contas únicas com data editada continuam marcadas como pagas
  const fetchPagamentos = useCallback(async () => {
    if (!user) return;
    try {
      setPagamentos(await api.get('/api/contas-pagar/pagamentos'));
    } catch {
      setPagamentos([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([fetchContas(), fetchPagamentos()]).finally(() => setLoading(false));
  }, [user, fetchContas, fetchPagamentos]);

  useEffect(() => {
    if (!user) return;
    const fetchAux = async () => {
      try {
        const [cats, bcs] = await Promise.all([
          api.get('/api/categorias'),
          api.get('/api/bancos'),
        ]);
        setCategorias(cats);
        setBancos(bcs);
      } catch {
        // sessão expirada: o PrivateRoute redireciona
      }
    };
    fetchAux();
  }, [user]);

  const categoriasDespesa = categorias.filter(c => c.tipo_lancamento === 'despesa' && c.ativo !== false);

  const limparFormulario = () => {
    setEditandoId(null);
    setDescricao('');
    setValor('');
    setRecorrente(true);
    setDiaVencimento(10);
    setDataVencimento('');
    setCategoriaId('');
    setBancoId('');
    formulario.onClose();
  };

  const abrirNova = () => {
    limparFormulario();
    formulario.onOpen();
  };

  const startEdit = (conta: ContaPagar) => {
    setEditandoId(conta.id);
    setDescricao(conta.descricao);
    setValor(formatarMoedaInput(conta.valor));
    setRecorrente(conta.recorrente);
    setDiaVencimento(conta.dia_vencimento || 10);
    setDataVencimento(conta.data_vencimento || '');
    setCategoriaId(conta.categoria_id || '');
    setBancoId(conta.banco_id || '');
    formulario.onOpen();
  };

  const handleSalvarConta = async () => {
    if (!user) return;
    if (!descricao.trim()) {
      toast({ title: 'Informe a descrição da conta', status: 'warning' });
      return;
    }
    const valorNum = moedaParaNumero(valor);
    if (!valor || isNaN(valorNum) || valorNum <= 0) {
      toast({ title: 'Valor inválido', status: 'warning' });
      return;
    }
    if (!categoriaId) {
      toast({ title: 'Selecione a categoria da despesa', status: 'warning' });
      return;
    }
    if (recorrente && (!diaVencimento || diaVencimento < 1 || diaVencimento > 31)) {
      toast({ title: 'Dia de vencimento deve ser entre 1 e 31', status: 'warning' });
      return;
    }
    if (!recorrente && !dataVencimento) {
      toast({ title: 'Informe a data de vencimento', status: 'warning' });
      return;
    }
    setLoading(true);
    const payload = {
      descricao: descricao.trim(),
      valor: valorNum,
      recorrente,
      dia_vencimento: recorrente ? diaVencimento : null,
      data_vencimento: recorrente ? null : dataVencimento,
      categoria_id: categoriaId,
      banco_id: bancoId || null,
      ativo: true,
    };
    try {
      if (editandoId) await api.put(`/api/contas-pagar/${editandoId}`, payload);
      else await api.post('/api/contas-pagar', payload);
      toast({ title: editandoId ? 'Conta atualizada!' : 'Conta criada!', status: 'success' });
      limparFormulario();
      await fetchContas();
    } catch (err) {
      toast({
        title: editandoId ? 'Erro ao atualizar conta' : 'Erro ao criar conta',
        description: err instanceof Error ? err.message : undefined,
        status: 'error',
      });
    }
    setLoading(false);
  };

  const abrirPagamento = (oc: Ocorrencia) => {
    setPayOcorrencia(oc);
    setPayBancoId(oc.conta.banco_id || '');
    setPayValor(formatarMoedaInput(oc.conta.valor));
    setPayData(dataHojeLocal());
    // Fatura de cartão se o nome disser isso: as compras já contaram uma a uma
    setPayFatura(/fatura|cart[ãa]o/i.test(oc.conta.descricao));
  };

  const handlePagar = async () => {
    if (!user || !payOcorrencia) return;
    const valorNum = moedaParaNumero(payValor);
    if (isNaN(valorNum) || valorNum <= 0) {
      toast({ title: 'Valor inválido', status: 'warning' });
      return;
    }
    if (!payBancoId) {
      toast({ title: 'Selecione o banco do pagamento', status: 'warning' });
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(payData)) {
      toast({ title: 'Data do pagamento inválida', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      // O servidor cria o lançamento e o registro de pagamento na mesma transação
      await api.post(`/api/contas-pagar/${payOcorrencia.conta.id}/pagar`, {
        mes: payOcorrencia.mes,
        ano: payOcorrencia.ano,
        valor: valorNum,
        banco_id: payBancoId,
        data: payData,
        pagamento_fatura: payFatura,
      });
      toast({ title: 'Conta paga!', status: 'success' });
      setPayOcorrencia(null);
      await fetchPagamentos();
    } catch (err) {
      toast({ title: 'Erro ao registrar o pagamento', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  // Mês em que a conta não foi cobrada (luz acumulada pro mês seguinte,
  // mensalidade suspensa): fecha a ocorrência sem criar despesa
  const handleSemCobranca = async () => {
    if (!user || !dispensaOc) return;
    setLoading(true);
    try {
      await api.post(`/api/contas-pagar/${dispensaOc.conta.id}/dispensar`, {
        mes: dispensaOc.mes,
        ano: dispensaOc.ano,
      });
      toast({ title: 'Mês marcado como sem cobrança', status: 'success' });
      setDispensaOc(null);
    } catch (err) {
      toast({ title: 'Erro ao marcar', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    await fetchPagamentos();
    setLoading(false);
  };

  const handleDesfazerPagamento = async (oc: Ocorrencia) => {
    if (!oc.pagamento?.id) return;
    setLoading(true);
    try {
      // O servidor remove o pagamento e o lançamento na mesma transação
      await api.delete(`/api/contas-pagar/pagamentos/${oc.pagamento.id}`);
      toast({ title: 'Pagamento desfeito', status: 'info' });
    } catch (err) {
      toast({ title: 'Erro ao desfazer pagamento', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    await fetchPagamentos();
    setLoading(false);
  };

  const handleExcluirConta = async () => {
    if (!deleteConta) return;
    setLoading(true);
    try {
      await api.delete(`/api/contas-pagar/${deleteConta.id}`);
      toast({ title: 'Conta excluída!', status: 'success' });
      if (editandoId === deleteConta.id) limparFormulario();
      await Promise.all([fetchContas(), fetchPagamentos()]);
    } catch (err) {
      toast({ title: 'Erro ao excluir conta', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setDeleteConta(null);
    setLoading(false);
  };

  const ocorrencias = ocorrenciasDoMes(contas, pagamentos, mes, ano, hoje);
  const totalMes = ocorrencias.reduce((acc, o) => acc + (o.paga ? Number(o.pagamento?.valor_pago ?? o.conta.valor) : o.conta.valor), 0);
  const totalPago = ocorrencias.filter(o => o.paga).reduce((acc, o) => acc + Number(o.pagamento?.valor_pago ?? o.conta.valor), 0);
  const totalPendente = totalMes - totalPago;

  // Compacta no celular: etiqueta curta numa linha só
  const statusTag = (oc: Ocorrencia, curta = false) => {
    const t = (cor: string, texto: string) => <Tag colorScheme={cor} size={curta ? 'sm' : 'md'} whiteSpace="nowrap" flexShrink={0}>{texto}</Tag>;
    if (oc.status === 'paga') return t('green', 'Paga');
    if (oc.status === 'sem_cobranca') return t('gray', 'Sem cobrança');
    if (oc.status === 'atrasada') return t('red', curta ? `${diasAte(oc.dataVencimento, hoje)}d atraso` : `Atrasada há ${diasAte(oc.dataVencimento, hoje)}d`);
    if (oc.status === 'vence_hoje') return t('orange', 'Vence hoje');
    return t('blue', `Em ${diasAte(hoje, oc.dataVencimento)}d`);
  };

  return (
    <Box maxW="1000px" mx="auto" mt={{ base: 0, md: 6 }} p={4} pt={{ base: 4, md: 4 }}>
      <Flex justify="space-between" align="center" mb={3} gap={2}>
        <Heading size="lg">Contas a pagar</Heading>
        <Button colorScheme="teal" size="sm" leftIcon={<AddIcon />} onClick={abrirNova}>Nova conta</Button>
      </Flex>

      <Flex mb={4} gap={2}>
        <Select size="sm" value={mes} onChange={e => setMes(Number(e.target.value))} w="auto">
          {meses.map((m, idx) => (
            <option value={idx} key={m}>{m}</option>
          ))}
        </Select>
        <Select size="sm" value={ano} onChange={e => setAno(Number(e.target.value))} w="auto">
          {[ano - 1, ano, ano + 1].map(a => (
            <option value={a} key={a}>{a}</option>
          ))}
        </Select>
      </Flex>

      {/* Cadastro/edição de conta fixa */}
      <Modal isOpen={formulario.isOpen} onClose={limparFormulario} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>{editandoId ? 'Editar conta' : 'Nova conta'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Descrição</FormLabel>
                <Input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Ex: Aluguel, Internet" />
              </FormControl>
              <Flex gap={3}>
                <FormControl>
                  <FormLabel>Valor (R$)</FormLabel>
                  <Input
                    type="text"
                    value={valor}
                    onChange={e => setValor(formatarMoedaInput(e.target.value))}
                    placeholder="0,00"
                    inputMode="decimal"
                    maxLength={17}
                    textAlign="right"
                  />
                </FormControl>
                {recorrente ? (
                  <FormControl>
                    <FormLabel>Dia do vencimento</FormLabel>
                    <Input type="number" min={1} max={31} value={diaVencimento} onChange={e => setDiaVencimento(Number(e.target.value))} />
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel>Vencimento</FormLabel>
                    <Input type="date" value={dataVencimento} onChange={e => setDataVencimento(e.target.value)} />
                  </FormControl>
                )}
              </Flex>
              <Checkbox isChecked={recorrente} onChange={e => setRecorrente(e.target.checked)}>
                Repete todo mês
              </Checkbox>
              <FormControl>
                <FormLabel>Categoria</FormLabel>
                <Select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} placeholder="Selecione">
                  {categoriasDespesa.map(cat => (
                    <option value={cat.id} key={cat.id}>{cat.nome}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Banco padrão (opcional)</FormLabel>
                <Select value={bancoId} onChange={e => setBancoId(e.target.value)} placeholder="Selecione">
                  {bancos.map(b => (
                    <option value={b.id} key={b.id}>{b.nome}</option>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={limparFormulario}>Cancelar</Button>
            <Button colorScheme="teal" onClick={handleSalvarConta} isLoading={loading}>
              {editandoId ? 'Salvar' : 'Adicionar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SimpleGrid columns={3} spacing={2} mb={5} p={3} borderWidth="1px" borderColor="gray.100" borderRadius="xl" boxShadow="sm">
        <Box>
          <Text fontSize="xs" color="gray.500">Total do mês</Text>
          <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'xl' }}>R$ {formatarMoeda(totalMes)}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500">Pago</Text>
          <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'xl' }} color="green.500">R$ {formatarMoeda(totalPago)}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500">Pendente</Text>
          <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'xl' }} color={totalPendente > 0 ? 'red.500' : 'green.500'}>R$ {formatarMoeda(totalPendente)}</Text>
        </Box>
      </SimpleGrid>

      <Heading size="sm" mb={2}>Contas de {meses[mes]}</Heading>
      {loading ? (
        <Spinner />
      ) : ocorrencias.length === 0 ? (
        <Text color="gray.500">Nenhuma conta neste mês. Use "Nova conta" para cadastrar suas contas fixas.</Text>
      ) : isMobile ? (
        <VStack align="stretch" spacing={2}>
          {ocorrencias.map(oc => {
            const [, mm, dd] = oc.dataVencimento.split('-');
            const categoria = categorias.find(c => c.id === oc.conta.categoria_id)?.nome;
            return (
              <Box key={`${oc.conta.id}-${oc.ano}-${oc.mes}`} p={3} borderWidth="1px" borderColor="gray.100" borderRadius="xl" boxShadow="sm" opacity={oc.paga ? 0.75 : 1}>
                <Flex gap={3} align="center">
                  <Box textAlign="center" minW="44px" py={1} borderRadius="md" bg={oc.status === 'atrasada' ? 'red.50' : 'gray.50'}>
                    <Text fontWeight="bold" lineHeight="1">{dd}</Text>
                    <Text fontSize="10px" color="gray.500" textTransform="uppercase">{meses[Number(mm) - 1].slice(0, 3)}</Text>
                  </Box>
                  <Box flex="1" minW={0}>
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{oc.conta.descricao}</Text>
                    <Flex gap={1} align="center" mt="2px">
                      {statusTag(oc, true)}
                      {categoria && <Text fontSize="xs" color="gray.500" noOfLines={1}>{categoria}</Text>}
                    </Flex>
                  </Box>
                  <Text fontWeight="bold" fontSize="sm" whiteSpace="nowrap">
                    {oc.semCobranca ? '—' : `R$ ${formatarMoeda(Number(oc.pagamento?.valor_pago ?? oc.conta.valor))}`}
                  </Text>
                  <Menu placement="bottom-end">
                    <MenuButton as={IconButton} aria-label={`Mais ações de ${oc.conta.descricao}`} icon={<Icon as={FaEllipsisV as any} />} variant="ghost" size="sm" />
                    <MenuList>
                      {!oc.paga && <MenuItem onClick={() => setDispensaOc(oc)}>Não veio cobrança este mês</MenuItem>}
                      <MenuItem onClick={() => startEdit(oc.conta)}>Editar conta</MenuItem>
                      <MenuItem color="red.500" onClick={() => setDeleteConta(oc.conta)}>Excluir conta</MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
                {oc.paga ? (
                  <Button size="xs" variant="link" mt={2} ml="56px" onClick={() => handleDesfazerPagamento(oc)}>
                    {oc.semCobranca ? 'Reabrir' : 'Desfazer pagamento'}
                  </Button>
                ) : (
                  <Button size="sm" colorScheme="green" variant="outline" w="100%" mt={3} leftIcon={<CheckIcon />} onClick={() => abrirPagamento(oc)}>
                    Marcar como paga
                  </Button>
                )}
              </Box>
            );
          })}
        </VStack>
      ) : (
        <Box overflowX="auto" maxW="100%">
          {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Venc.</Th>
                <Th>Descrição</Th>
                {!isMobile && <Th>Categoria</Th>}
                {!isMobile && <Th>Recorrência</Th>}
                <Th isNumeric>Valor</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ocorrencias.map(oc => (
                <Tr key={`${oc.conta.id}-${oc.ano}-${oc.mes}`}>
                  <Td>{isMobile ? oc.dataVencimento.split('-')[2] : formatarDataBR(oc.dataVencimento)}</Td>
                  <Td>{oc.conta.descricao}</Td>
                  {!isMobile && <Td>{categorias.find(c => c.id === oc.conta.categoria_id)?.nome || '-'}</Td>}
                  {!isMobile && <Td>{oc.conta.recorrente ? <Tag colorScheme="purple">Mensal</Tag> : <Tag colorScheme="gray">Única</Tag>}</Td>}
                  <Td isNumeric whiteSpace="nowrap">
                    {oc.semCobranca ? '—' : `R$ ${formatarMoeda(Number(oc.pagamento?.valor_pago ?? oc.conta.valor))}`}
                  </Td>
                  <Td>{statusTag(oc)}</Td>
                  <Td whiteSpace="nowrap">
                    {oc.paga ? (
                      <Button size="xs" variant="outline" onClick={() => handleDesfazerPagamento(oc)}>
                        {oc.semCobranca ? 'Reabrir' : 'Desfazer'}
                      </Button>
                    ) : (
                      <>
                        <IconButton aria-label="Pagar" icon={<CheckIcon />} size="sm" colorScheme="green" variant="ghost" onClick={() => abrirPagamento(oc)} />
                        <IconButton
                          aria-label="Marcar mês sem cobrança"
                          title="Não veio cobrança neste mês"
                          icon={<MinusIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => setDispensaOc(oc)}
                        />
                      </>
                    )}
                    <IconButton aria-label="Editar" icon={<EditIcon />} size="sm" variant="ghost" onClick={() => startEdit(oc.conta)} />
                    <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="sm" colorScheme="red" variant="ghost" onClick={() => setDeleteConta(oc.conta)} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Diálogo de pagamento */}
      <AlertDialog isOpen={!!payOcorrencia} leastDestructiveRef={cancelRef} onClose={() => setPayOcorrencia(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Pagar {payOcorrencia?.conta.descricao}
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl mb={4}>
                <FormLabel>Valor pago</FormLabel>
                <Input
                  type="text"
                  value={payValor}
                  onChange={e => setPayValor(formatarMoedaInput(e.target.value))}
                  inputMode="decimal"
                  maxLength={17}
                  textAlign="right"
                />
              </FormControl>
              <FormControl mb={4}>
                <FormLabel>Banco</FormLabel>
                <Select value={payBancoId} onChange={e => setPayBancoId(e.target.value)} placeholder="Selecione o banco">
                  {bancos.map(b => (
                    <option value={b.id} key={b.id}>{b.nome}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Data do pagamento</FormLabel>
                <Input type="date" value={payData} onChange={e => setPayData(e.target.value)} />
              </FormControl>
              <Checkbox mt={4} isChecked={payFatura} onChange={e => setPayFatura(e.target.checked)}>
                É pagamento de fatura de cartão
              </Checkbox>
              <Text fontSize="sm" color="gray.500" mt={3}>
                {payFatura
                  ? 'O lançamento debita do banco escolhido mas NÃO conta como despesa nova — cada compra da fatura já contou na sua categoria.'
                  : 'Será criado um lançamento de despesa nessa data, debitado do banco escolhido.'}
                {' '}Pagou dias atrás? Ajuste a data pra bater com o extrato.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setPayOcorrencia(null)}>Cancelar</Button>
              <Button colorScheme="green" onClick={handlePagar} ml={3} isLoading={loading}>
                Confirmar pagamento
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Diálogo de mês sem cobrança */}
      <AlertDialog isOpen={!!dispensaOc} leastDestructiveRef={cancelRef} onClose={() => setDispensaOc(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Mês sem cobrança</AlertDialogHeader>
            <AlertDialogBody>
              Marcar "{dispensaOc?.conta.descricao}" de {dispensaOc && meses[dispensaOc.mes]} como
              sem cobrança? Nenhuma despesa é criada e o lembrete para de avisar.
              Use quando a fatura não veio neste mês — acumulada pro mês
              seguinte, cobrança suspensa ou adiada.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDispensaOc(null)}>Cancelar</Button>
              <Button colorScheme="gray" onClick={handleSemCobranca} ml={3} isLoading={loading}>
                Marcar sem cobrança
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Diálogo de exclusão */}
      <AlertDialog isOpen={!!deleteConta} leastDestructiveRef={cancelRef} onClose={() => setDeleteConta(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir conta</AlertDialogHeader>
            <AlertDialogBody>
              Excluir "{deleteConta?.descricao}"? {deleteConta?.recorrente ? 'Todas as ocorrências futuras deixarão de aparecer.' : ''} Os
              lançamentos de pagamentos já feitos serão mantidos.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteConta(null)}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleExcluirConta} ml={3} isLoading={loading}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ContasPagar;
